import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import cinesisLogo from '../../assets/cinesis-logo.png';
import { isAdmin } from '../../utils/adminEmails';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const getNavLinks = () => {
    if (!user) return [];
    if (user.account_type === "driver") {
      return [
        { to: "/my-loads", label: "My Loads", icon: "fas fa-question-circle" },
        user && {
          to: "/quick-start",
          label: "Help",
          icon: "fas fa-book",
        },
      ].filter(Boolean);
    }

    const links = [
      { to: "/", label: "Load Search", icon: "fas fa-search" },
      { to: "/saved-loads", label: "My Loads", icon: "fas fa-bookmark" },
      { to: "/documents", label: "Documents", icon: "fas fa-file-alt" },
      user &&
        user.account_type === "carrier" && {
          to: "/ai-notifications",
          label: "AI Notifications",
          icon: "fas fa-bell",
        },
      user &&
        user.account_type === "carrier" && {
          to: "/team",
          label: "Team",
          icon: "fas fa-users",
        },
      user &&
        user.account_type === "no account" && {
          to: "/ai-voice-agent",
          label: "AI Voice Agent",
          icon: "fas fa-phone",
        },
      user &&
        user.account_type === "dispatcher" && {
          to: "/carriers",
          label: "Carriers",
          icon: "fas fa-truck-moving",
        },
      user && {
        to: "/quick-start",
        label: "Help",
        icon: "fas fa-question-circle",
      },
      user && { to: "/subscribe", label: "Subscribe", icon: "fas fa-tag" },
      user && {
        to: "/contact-us",
        label: "Contact Us",
      },
      isAdmin(user) && {
        to: "/admin",
        label: "Admin",
        icon: "fas fa-shield-alt",
      },
    ].filter(Boolean);
    return links;
  };

  const handleLogout = () => {
    logout();
  };

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-52 bg-tw-neutral-900 text-white flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="min-h-20 py-4 px-4 border-b border-tw-neutral-800 flex items-center">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center">
              <img
                src={cinesisLogo}
                alt="Cinesis Logo"
                className="h-full w-full object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <span className="text-xl font-bold text-white leading-none">Cinesis</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2">
          {getNavLinks()
            .filter(
              (link) => link.label !== "Contact Us" && link.label !== "Help",
            )
            .map((link, index) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={index}
                  to={link.to}
                  onClick={handleLinkClick}
                  className={`block px-4 py-2.5 text-base transition-colors ${
                    isActive
                      ? 'bg-tw-neutral-800 text-white'
                      : 'text-tw-neutral-300'
                  }`}
                  style={{ 
                    color: isActive ? 'white' : '#ced4da',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#212529';
                      e.currentTarget.style.color = '#ced4da';
                      e.currentTarget.style.textDecoration = 'none';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#ced4da';
                      e.currentTarget.style.textDecoration = 'none';
                    }
                  }}
                >
                  {link.icon && <i className={`${link.icon} mr-2`}></i>}
                  {link.label}
                </Link>
              );
            })}
        </nav>

        {/* Bottom links */}
        <div className="border-t border-tw-neutral-800 py-2">
          {getNavLinks()
            .filter((link) => link.label === "Help")
            .map((link, index) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={index}
                  to={link.to}
                  onClick={handleLinkClick}
                  className={`block px-4 py-2.5 text-base transition-colors ${
                    isActive ? 'text-white' : 'text-tw-neutral-300'
                  }`}
                  style={{ 
                    color: '#ced4da',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#212529';
                    e.currentTarget.style.color = '#ced4da';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#ced4da';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {link.icon && <i className={`${link.icon} mr-2`}></i>}
                  {link.label}
                </Link>
              );
            })}
          {getNavLinks()
            .filter((link) => link.label === "Contact Us")
            .map((link, index) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={index}
                  to={link.to}
                  onClick={handleLinkClick}
                  className={`block px-4 py-2.5 text-base transition-colors ${
                    isActive ? 'text-white' : 'text-tw-neutral-300'
                  }`}
                  style={{ 
                    color: '#ced4da',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#212529';
                    e.currentTarget.style.color = '#ced4da';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#ced4da';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {link.icon && <i className={`${link.icon} mr-2`}></i>}
                  {link.label}
                </Link>
              );
            })}
          <Link
            to="/profile"
            onClick={handleLinkClick}
            className={`block px-4 py-2.5 text-base transition-colors ${
              location.pathname === '/profile' ? 'text-white' : 'text-tw-neutral-300'
            }`}
            style={{ 
              color: '#ced4da',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#212529';
              e.currentTarget.style.color = '#ced4da';
              e.currentTarget.style.textDecoration = 'none';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#ced4da';
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            My Account
          </Link>
          <div className="px-4 mt-3">
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Log out"
              className="w-full rounded bg-red-600 text-white font-semibold text-base py-2 transition-colors hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
